import React, { useState, useEffect } from "react";
import {
  Box,
  Card,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  CircularProgress,
  Button,
  Paper,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Autocomplete,
} from "@mui/material";
import { useHistory } from "react-router-dom";
import useApiCall from "../common/api-call";
import {
  listPremades,
  reloadPremades,
  targetPremades,
  listAspects,
} from "./premade-service.js";
import { useToast } from "../common/toast";
import PremadeScriptDialog from "./premade-script-dialog";

const PremadeManageView = () => {
  const { apiCall } = useApiCall();
  const history = useHistory();
  const [premades, setPremades] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [envFilter, setEnvFilter] = useState("both");
  const [capabilitySearch, setCapabilitySearch] = useState([]);
  const [functionSearch, setFunctionSearch] = useState([]);
  const [isTargetSearch, setIsTargetSearch] = useState(false);
  const [aspects, setAspects] = useState({
    capabilities: [],
    functions: [],
    integrations: [],
  });
  const [scriptDialogOpen, setScriptDialogOpen] = useState(false);
  const [selectedPremade, setSelectedPremade] = useState(null);
  const { errorToast, successToast } = useToast();

  useEffect(() => {
    fetchData(false);
    loadAspects();
  }, []);

  const loadAspects = async () => {
    try {
      const aspectsResult = await listAspects(apiCall);
      if (aspectsResult) {
        setAspects({
          capabilities: aspectsResult.capabilities || [],
          functions: aspectsResult.functions || [],
          integrations: aspectsResult.integrations || [],
        });
      }
    } catch (error) {
      console.error("Failed to load aspects:", error);
    }
  };

  const fetchData = async (targetSearch = false) => {
    setLoading(true);
    try {
      let premadeMap = new Map();

      if (
        targetSearch &&
        (capabilitySearch.length > 0 || functionSearch.length > 0)
      ) {
        // Use target search
        const targetResult = await targetPremades(
          apiCall,
          capabilitySearch,
          functionSearch,
        );

        if (targetResult?.premades) {
          targetResult.premades.forEach(premade => {
            const key = premade.id;
            if (!premadeMap.has(key)) {
              premadeMap.set(key, {
                id: premade.id,
                description: premade.description,
                versions: [],
              });
            }
            premadeMap.get(key).versions.push({
              env: premade.version === "prod" ? "prod" : "stg",
              version: premade.version,
            });
          });
        }
      } else {
        // Use regular list search
        const [prodResult, stgResult] = await Promise.all([
          listPremades(apiCall, "prod"),
          listPremades(apiCall, "stg"),
        ]);

        // Process prod data
        if (prodResult?.premades) {
          prodResult.premades.forEach(premade => {
            const key = premade.id;
            if (!premadeMap.has(key)) {
              premadeMap.set(key, {
                id: premade.id,
                description: premade.description,
                versions: [],
              });
            }
            premadeMap.get(key).versions.push({
              env: "prod",
              version: premade.version,
            });
          });
        }

        // Process stg data
        if (stgResult?.premades) {
          stgResult.premades.forEach(premade => {
            const key = premade.id;
            if (!premadeMap.has(key)) {
              premadeMap.set(key, {
                id: premade.id,
                description: premade.description,
                versions: [],
              });
            }
            premadeMap.get(key).versions.push({
              env: "stg",
              version: premade.version,
            });
          });
        }
      }

      // Convert map to array and sort by ID
      const mergedPremades = Array.from(premadeMap.values()).sort((a, b) =>
        a.id.localeCompare(b.id),
      );

      setPremades(mergedPremades);
    } catch (error) {
      console.error("Failed to fetch premades:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleReload = async () => {
    try {
      const resp = await reloadPremades(apiCall);
      if (resp && !resp.error) {
        setIsTargetSearch(false);
        setCapabilitySearch([]);
        setFunctionSearch([]);
        await fetchData(false);
        await loadAspects();
      } else {
        errorToast("Error");
      }
    } catch (error) {
      errorToast("Error");
    }
  };

  const handleSearch = () => {
    setIsTargetSearch(true);
    fetchData(true);
  };

  const handleClearSearch = () => {
    setIsTargetSearch(false);
    setCapabilitySearch([]);
    setFunctionSearch([]);
    fetchData(false);
  };

  const filteredPremades = premades.filter(premade => {
    // First apply search filter
    const matchesSearch =
      premade.id?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      premade.description?.toLowerCase().includes(searchTerm.toLowerCase());

    if (!matchesSearch) return false;

    // Then apply environment filter
    if (envFilter === "both") return true;

    // Check if premade has the selected environment
    return premade.versions.some(version => version.env === envFilter);
  });

  const handleOpenPremade = (premadeId, env) => {
    // Find the version for the specific environment
    const premade = premades.find(p => p.id === premadeId);
    if (premade) {
      const versionInfo = premade.versions.find(v => v.env === env);
      if (versionInfo) {
        setSelectedPremade({
          premadeId: premadeId,
          version: versionInfo.version,
        });
        setScriptDialogOpen(true);
      }
    }
  };

  const getVersionButtons = (premadeId, versions) => {
    const prodVersion = versions.find(v => v.env === "prod");
    const stgVersion = versions.find(v => v.env === "stg");

    return (
      <Box sx={{ display: "flex", gap: 1 }}>
        {prodVersion && (envFilter === "both" || envFilter === "prod") && (
          <Button
            size="small"
            variant="contained"
            color="primary"
            onClick={() => handleOpenPremade(premadeId, "prod")}
            sx={{ minWidth: 60 }}
          >
            Prod
          </Button>
        )}
        {stgVersion && (envFilter === "both" || envFilter === "stg") && (
          <Button
            size="small"
            variant="contained"
            color="secondary"
            onClick={() => handleOpenPremade(premadeId, "stg")}
            sx={{ minWidth: 60 }}
          >
            Stg
          </Button>
        )}
      </Box>
    );
  };

  return (
    <Box sx={{ padding: 2 }}>
      {loading ? (
        <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
          <CircularProgress size={40} />
        </Box>
      ) : (
        <>
          {/* Search criteria row */}
          <Box sx={{ mb: 2, display: "flex", gap: 2, alignItems: "center" }}>
            <Autocomplete
              multiple
              size="small"
              options={aspects.capabilities}
              value={capabilitySearch}
              onChange={(event, newValue) => setCapabilitySearch(newValue)}
              renderInput={params => (
                <TextField
                  {...params}
                  label="Capabilities"
                  placeholder="Select capabilities..."
                />
              )}
              sx={{ minWidth: 250 }}
            />
            <Autocomplete
              multiple
              size="small"
              options={aspects.functions}
              value={functionSearch}
              onChange={(event, newValue) => setFunctionSearch(newValue)}
              renderInput={params => (
                <TextField
                  {...params}
                  label="Functions"
                  placeholder="Select functions..."
                />
              )}
              sx={{ minWidth: 250 }}
            />
            <Button
              variant="contained"
              color="secondary"
              onClick={handleSearch}
              disabled={
                capabilitySearch.length === 0 && functionSearch.length === 0
              }
              sx={{ flexShrink: 0 }}
            >
              Search
            </Button>
            {isTargetSearch && (
              <Button
                variant="outlined"
                onClick={handleClearSearch}
                sx={{ flexShrink: 0 }}
              >
                Clear
              </Button>
            )}
          </Box>

          {/* Filter and action buttons row */}
          <Box sx={{ mb: 2, display: "flex", gap: 2, alignItems: "center" }}>
            <FormControl size="small" sx={{ minWidth: 120, flexShrink: 0 }}>
              <InputLabel>Environment</InputLabel>
              <Select
                value={envFilter}
                label="Environment"
                onChange={e => setEnvFilter(e.target.value)}
              >
                <MenuItem value="both">Any</MenuItem>
                <MenuItem value="prod">Prod</MenuItem>
                <MenuItem value="stg">Stg</MenuItem>
              </Select>
            </FormControl>
            <Button
              variant="outlined"
              color="primary"
              onClick={handleReload}
              sx={{ flexShrink: 0 }}
            >
              Reload
            </Button>
            <Button
              variant="contained"
              color="primary"
              onClick={() => {
                setSelectedPremade({
                  premadeId: null,
                  version: "draft",
                });
                setScriptDialogOpen(true);
              }}
              sx={{ flexShrink: 0 }}
            >
              Add
            </Button>
            <Paper elevation={0} sx={{ flexGrow: 1 }}>
              <TextField
                fullWidth
                variant="outlined"
                placeholder="Filter by ID or description..."
                value={searchTerm}
                onChange={e => setSearchTerm(e.target.value)}
                size="small"
                sx={{
                  "& .MuiOutlinedInput-root": {
                    backgroundColor: "background.paper",
                  },
                }}
              />
            </Paper>
          </Box>

          <TableContainer component={Paper} elevation={1}>
            <Table sx={{ minWidth: 650 }} size="medium">
              <TableHead>
                <TableRow>
                  <TableCell sx={{ fontWeight: "bold", width: "25%" }}>
                    ID
                  </TableCell>
                  <TableCell sx={{ fontWeight: "bold", width: "45%" }}>
                    Description
                  </TableCell>
                  <TableCell sx={{ fontWeight: "bold", width: "30%" }}>
                    Versions
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {filteredPremades.map(premade => (
                  <TableRow
                    key={premade.id}
                    sx={{
                      "&:hover": {
                        backgroundColor: "action.hover",
                      },
                    }}
                  >
                    <TableCell sx={{ verticalAlign: "top" }}>
                      <Typography
                        variant="body2"
                        sx={{ fontFamily: "monospace" }}
                      >
                        {premade.id}
                      </Typography>
                    </TableCell>
                    <TableCell sx={{ verticalAlign: "top" }}>
                      <Typography variant="body2">
                        {premade.description || "No description"}
                      </Typography>
                    </TableCell>
                    <TableCell sx={{ verticalAlign: "top" }}>
                      {getVersionButtons(premade.id, premade.versions)}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>

          {filteredPremades.length === 0 && !loading && (
            <Box sx={{ textAlign: "center", py: 4 }}>
              <Typography variant="body2" color="text.secondary">
                {searchTerm
                  ? "No premades match your search"
                  : "No premades found"}
              </Typography>
            </Box>
          )}

          <Box sx={{ mt: 2 }}>
            <Typography variant="caption" color="text.secondary">
              {isTargetSearch ? (
                <>
                  Search results: {filteredPremades.length} premade
                  {filteredPremades.length !== 1 ? "s" : ""}
                </>
              ) : (
                <>
                  Total: {filteredPremades.length} premade
                  {filteredPremades.length !== 1 ? "s" : ""}
                </>
              )}
            </Typography>
          </Box>
        </>
      )}

      <PremadeScriptDialog
        open={scriptDialogOpen}
        onClose={() => {
          setScriptDialogOpen(false);
          setSelectedPremade(null);
          // Refresh the data to show any changes
          fetchData(isTargetSearch);
        }}
        premadeId={selectedPremade?.premadeId}
        version={selectedPremade?.version}
      />
    </Box>
  );
};

export default PremadeManageView;
